import { useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { useCachedState, useRefreshVersion } from './store';
import { CostByDay, RendersByDay, StatusDonut } from './charts';
import { BackendBadge, Badge, Empty, ErrorState, Metric, Select, StateBadge, date, duration, money, useMe } from './ui';
import { canOpenAccounts, openCustomerAccount } from './Users';

const RANGES = [
  ['7', 'Last 7 days'], ['30', 'Last 30 days'], ['90', 'Last 90 days'], ['all', 'All time'], ['custom', 'Custom range'],
];
const PAGE_SIZE = 25;

const EMPTY_FILTERS = { search: '', renderStatus: '', transcriptionStatus: '', provider: '', range: '30', from: '', to: '' };

/**
 * Turns the form state into query params. A preset range is computed at
 * request time so "last 7 days" stays true across a long-open tab; a custom
 * range is sent as whole days in the operator's own timezone.
 */
const toQuery = (filters) => {
  const params = {};
  if (filters.search.trim()) params.search = filters.search.trim();
  if (filters.renderStatus) params.renderStatus = filters.renderStatus;
  if (filters.transcriptionStatus) params.transcriptionStatus = filters.transcriptionStatus;
  if (filters.provider) params.provider = filters.provider;
  if (filters.range === 'custom') {
    if (filters.from) params.from = new Date(`${filters.from}T00:00:00`).toISOString();
    if (filters.to) params.to = new Date(`${filters.to}T23:59:59.999`).toISOString();
  } else if (filters.range === 'all') {
    // The stats endpoint defaults to 30 days when no range is sent, so "all"
    // has to be explicit. Nothing predates the product.
    params.from = new Date('2024-01-01T00:00:00Z').toISOString();
    params.to = new Date().toISOString();
  } else {
    params.from = new Date(Date.now() - Number(filters.range) * 24 * 60 * 60 * 1000).toISOString();
    params.to = new Date().toISOString();
  }
  return params;
};

export function Videos({ onOpenIssues, initialSearch }) {
  // Arriving from an incident link: search for that project across all time,
  // since the incident may be older than the default 30-day window.
  const initial = initialSearch ? { ...EMPTY_FILTERS, search: initialSearch, range: 'all' } : EMPTY_FILTERS;
  const [draft, setDraft] = useState(initial);
  const [applied, setApplied] = useState(initial);
  const [page, setPage] = useState(0);
  const initialQuery = toQuery(initial);
  const initialQs = new URLSearchParams({ ...initialQuery, limit: PAGE_SIZE, offset: 0 }).toString();
  const initialStatsQs = new URLSearchParams(initialQuery).toString();
  const [list, setList] = useCachedState(`/videos?${initialQs}`);
  const [stats, setStats] = useCachedState(`/videos/stats?${initialStatsQs}`);
  const [loading, setLoading] = useState(!list || !stats);
  const [error, setError] = useState('');
  const refreshVersion = useRefreshVersion();
  const me = useMe();

  const query = useMemo(() => toQuery(applied), [applied]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    const qs = (extra = {}) => new URLSearchParams({ ...query, ...extra }).toString();
    Promise.all([
      api(`/videos?${qs({ limit: PAGE_SIZE, offset: page * PAGE_SIZE })}`),
      api(`/videos/stats?${qs()}`),
    ]).then(([nextList, nextStats]) => {
      if (cancelled) return;
      setList(nextList); setStats(nextStats);
    }).catch((caught) => {
      if (!cancelled) setError(caught.message);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [query, page, refreshVersion]);

  const set = (key) => (event) => setDraft((prev) => ({ ...prev, [key]: event.target.value }));
  const apply = (next = draft) => { setPage(0); setApplied(next); };
  // The search box filters as you type, a beat after the last keystroke —
  // Enter and the button still work, but nobody should have to know that.
  useEffect(() => {
    if (draft.search === applied.search) return undefined;
    const timer = setTimeout(() => apply(draft), 400);
    return () => clearTimeout(timer);
  }, [draft.search]); // eslint-disable-line react-hooks/exhaustive-deps
  // Selects apply immediately; the text search and custom dates wait for
  // Enter or the button so a half-typed email doesn't fire a query per key.
  const setAndApply = (key) => (event) => {
    const next = { ...draft, [key]: event.target.value };
    setDraft(next); apply(next);
  };
  const reset = () => { setDraft(EMPTY_FILTERS); apply(EMPTY_FILTERS); };

  const activeCount = ['search', 'renderStatus', 'transcriptionStatus', 'provider'].filter((k) => applied[k]).length + (applied.range !== '30' ? 1 : 0);
  const total = list?.page?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const t = stats?.totals;
  // The rate is history and stays put — a failed render failed, whatever was
  // done about it since. What moves when an incident is resolved is the
  // review count and the red in the charts: `error` from the API is every
  // failure, so split off the ones already dealt with.
  const successRate = t?.total ? Math.round((t.done / t.total) * 100) : null;
  const splitFailures = (row) => row && ({ ...row, error: row.error - row.errorResolved, resolved: row.errorResolved });
  const outcomes = splitFailures(t);
  const daily = useMemo(() => stats?.daily?.map(splitFailures), [stats]);
  const openFailures = outcomes?.error ?? 0;

  return <>
    <section className="page-title"><div><div className="eyebrow">Videos</div><h1>Videos</h1><p>What ran, where it ran, what it cost, and what needs attention.</p></div>{activeCount > 0 && <button className="subtle" onClick={reset}>Clear {activeCount} filter{activeCount > 1 ? 's' : ''}</button>}</section>

    <section className="toolbar">
      <input value={draft.search} onChange={set('search')} onKeyDown={(e) => e.key === 'Enter' && apply()} placeholder="Search title, email, or project ID" />
      <Select value={draft.renderStatus} onChange={setAndApply('renderStatus')} label="Render state" options={[['', 'All render states'], ['rendering', 'Rendering'], ['done', 'Rendered'], ['error', 'Failed']]} />
      <Select value={draft.transcriptionStatus} onChange={setAndApply('transcriptionStatus')} label="Transcription state" options={[['', 'All transcription states'], ['queued', 'Queued'], ['running', 'Running'], ['done', 'Done'], ['error', 'Failed']]} />
      <Select value={draft.provider} onChange={setAndApply('provider')} label="Provider" options={[['', 'All providers'], ['groq', 'Groq'], ['openai', 'OpenAI'], ['openrouter', 'OpenRouter'], ['cloudrun', 'Cloud Run']]} />
      <Select value={draft.range} onChange={setAndApply('range')} label="Date range" options={RANGES} />
      {draft.range === 'custom' && <>
        <input type="date" value={draft.from} max={draft.to || undefined} onChange={set('from')} />
        <input type="date" value={draft.to} min={draft.from || undefined} onChange={set('to')} />
      </>}
      <button className="subtle" onClick={() => apply()}>Apply filters</button>
    </section>

    {error && <ErrorState message={error} retry={() => apply({ ...applied })} />}

    <section className={`metric-grid ${loading ? 'is-loading' : ''}`}>
      <Metric label="Renders in range" value={t?.total ?? '—'} detail={`${t?.done ?? 0} rendered · ${t?.error ?? 0} failed · ${t?.rendering ?? 0} in progress`} />
      <Metric label="Success rate" value={successRate == null ? '—' : `${successRate}%`}
        detail={openFailures ? `${openFailures} failure${openFailures > 1 ? 's' : ''} to review` : t?.error ? `All ${t.error} failure${t.error > 1 ? 's' : ''} resolved` : 'No failures in range'}
        alert={openFailures > 0} />
      <Metric label="Render time (p50)" value={duration(stats?.latency?.p50Ms)} detail={`p95 ${duration(stats?.latency?.p95Ms)} · max ${duration(stats?.latency?.maxMs)}`} />
      <Metric label="Estimated spend" value={money((stats?.cost?.renderUsd ?? 0) + (stats?.cost?.transcriptionUsd ?? 0))} detail={`Render ${money(stats?.cost?.renderUsd)} · Transcription ${money(stats?.cost?.transcriptionUsd)}`} />
    </section>

    <section className={`chart-grid ${loading ? 'is-loading' : ''}`}>
      <article className="panel"><div className="panel-heading"><div><span className="eyebrow">RENDER VOLUME</span><h2>Renders per day</h2></div></div>
        {daily?.length ? <RendersByDay daily={daily} /> : <Empty title="No renders in this range" body="Widen the date range or clear a filter." />}
      </article>
      <article className="panel"><div className="panel-heading"><div><span className="eyebrow">OUTCOMES</span><h2>Status share</h2></div></div>
        <StatusDonut totals={outcomes ?? { total: 0 }} />
      </article>
      <article className="panel"><div className="panel-heading"><div><span className="eyebrow">RENDER SPEND</span><h2>Estimated cost per day</h2></div></div>
        {stats?.daily?.length ? <CostByDay daily={stats.daily} /> : <Empty title="No cost events in this range" body="The cost ledger fills as renders complete." />}
      </article>
      <article className="panel"><div className="panel-heading"><div><span className="eyebrow">PROVIDERS</span><h2>Usage in range</h2></div></div>
        {stats?.providers?.length ? <div className="provider-list">{stats.providers.map((p) => <div className="provider" key={p.provider}><div className="provider-name"><span className={`dot ${p.attempts && p.succeeded / p.attempts < 0.9 ? 'warn' : 'good'}`} />{p.provider}</div><div><b>{p.attempts}</b><small> attempts</small></div><div><b>{p.attempts ? Math.round((p.succeeded / p.attempts) * 100) : 0}%</b><small> success</small></div><div><b>{money(p.costUsd)}</b><small> cost</small></div></div>)}</div> : <Empty title="No provider activity" body="Usage events appear once a matching pipeline runs." />}
      </article>
    </section>

    <article className={`panel table-panel ${loading ? 'is-loading' : ''}`}>
      <div className="panel-heading"><div><span className="eyebrow">PROJECTS</span><h2>{total} matching video{total === 1 ? '' : 's'}</h2></div>
        <div className="pager"><button className="subtle" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>← Prev</button><span>Page {page + 1} of {pages}</span><button className="subtle" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>Next →</button></div></div>
      <div className="table-wrap"><table><thead><tr><th>Video</th><th>Customer</th><th>Transcription</th><th>Render</th><th>Cost</th><th>Last activity</th><th>Issues</th></tr></thead><tbody>
        {list?.items?.length ? list.items.map((row) => <tr key={row.id}>
          <td><b title={row.title || 'Untitled project'}>{row.title || 'Untitled project'}</b><small title={row.id}>{row.id}</small>
            {row.voiceover_audio_id && <small className="plain voice-line" title={`${row.voiceover_title} — ${row.voiceover_voice}${row.voiceover_mutes_source ? ', video sound muted' : ', mixed with video sound'}`}><Badge tone="info">AI voiceover</Badge> {row.voiceover_voice ? `${row.voiceover_voice[0].toUpperCase()}${row.voiceover_voice.slice(1)}` : ''}{row.voiceover_duration_sec ? ` · ${Math.round(row.voiceover_duration_sec)} s` : ''}</small>}
            {row.expired_at && <small className="expired-note" title={`Files removed by retention ${date(row.expired_at)}`}>Files removed · {date(row.expired_at)}</small>}</td>
          <td><b>{row.user_email}</b><small>{row.duration_sec ? `${Math.round(row.duration_sec)} sec` : 'Duration pending'}</small>
            {canOpenAccounts(me) && <button className="text-button account-link" title="Sign in to this customer's account in a new tab (audited)" onClick={() => openCustomerAccount({ id: row.user_id, email: row.user_email })}>Open account ↗</button>}</td>
          <td><StateBadge status={row.transcription_status} /><small>{row.transcription_provider ? `${row.transcription_provider} · ${row.transcription_model}` : '—'}</small></td>
          <td><StateBadge status={row.render_status} />
            {row.render_status && <BackendBadge backend={row.render_backend} />}
            <small>{row.render_status === 'done' ? 'Rendered' : row.render_progress != null ? `${row.render_progress}% complete` : 'Not requested'}</small></td>
          <td><b>{money(Number(row.transcription_cost_usd) + Number(row.render_cost_usd))}</b><small>Tx {money(row.transcription_cost_usd)} · R {money(row.render_cost_usd)}</small></td>
          <td><span>{date(row.render_updated_at || row.transcription_updated_at || row.updated_at)}</span></td>
          <td>{Number(row.open_issue_count) > 0
            ? <button className="issue-link" onClick={() => onOpenIssues(row)}>{row.open_issue_count} open issue{row.open_issue_count > 1 ? 's' : ''} →</button>
            : Number(row.closed_issue_count) > 0
              // Resolved and gone from the queue, but still on record: an
              // operator looking at a render that failed yesterday and works
              // today should see "closed", not "none".
              ? <button className="issue-link closed" title="Every issue on this video is resolved — click to review" onClick={() => onOpenIssues(row)}>Closed ({row.closed_issue_count}) →</button>
              : <Badge>None</Badge>}</td>
        </tr>) : <tr><td colSpan="7"><Empty title="No videos match these filters" body="Try clearing a filter or widening the date range." /></td></tr>}
      </tbody></table></div>
    </article>
  </>;
}
